// Front-page card: the latest business briefing. Briefings > History has the
// rest, plus the business context and the schedule.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/auth';
import { Button } from '../lib/ui';
import { briefingTitle, formatWhen, isStale, unfilled } from '../lib/briefings';
import BriefingView, { SourcePill } from './BriefingView';
import './Briefings.css';

const LS_OPEN = 'farmos.briefingOpen';
const REFRESH_MS = 10 * 60_000;

const readOpen = () => { try { return localStorage.getItem(LS_OPEN) !== '0'; } catch { return true; } };

export default function BriefingCard() {
  const [state, setState] = useState({ status: 'loading', briefing: null, gaps: [] });
  const [open, setOpen] = useState(readOpen);
  const loadedAt = useRef(0);

  const load = useCallback(async () => {
    loadedAt.current = Date.now();
    try {
      const [{ data }, ctx] = await Promise.all([
        api.get('/business/briefings/latest'),
        api.get('/business/context').catch(() => null),
      ]);
      setState({ status: 'ready', briefing: data.briefing, gaps: ctx ? unfilled(ctx.data.context, ctx.data.sections) : [] });
    } catch (e) {
      // 404: the server hasn't got the briefing routes yet, so say nothing
      setState(s => ({ ...s, status: e.response?.status === 404 ? 'missing' : 'error' }));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    const onFocus = () => { if (Date.now() - loadedAt.current > 60_000) load(); };
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [load]);

  const toggle = () => setOpen(o => {
    try { localStorage.setItem(LS_OPEN, o ? '0' : '1'); } catch { /* private mode */ }
    return !o;
  });

  if (state.status === 'loading' || state.status === 'missing') return null;
  const b = state.briefing;
  const stale = state.status === 'ready' && isStale(b);

  return (
    <section className={`br-card${stale || b?.source === 'numbers' ? ' br-card--warn' : ''}`} aria-label="Latest briefing">
      <div className="br-card__head">
        <h2 className="br-card__title">{b ? briefingTitle(b) : 'Briefings'}</h2>
        {b && <span className="br-card__when">{formatWhen(b.startedAt)}</span>}
        {b && <SourcePill briefing={b} />}
        <span className="br-card__spacer" />
        <Link className="br-card__link" to="/briefings">History</Link>
        {b && <Button size="sm" variant="muted" onClick={toggle}>{open ? 'Hide' : 'Show'}</Button>}
      </div>

      {state.status === 'error' && <p className="br-card__empty">Couldn't load the latest briefing.</p>}
      {state.status === 'ready' && !b && (
        <p className="br-card__empty">No briefings yet. They're written at the times set in <Link to="/briefings?tab=schedule">Briefings</Link>, or write a test one there now.</p>
      )}
      {b && open && <BriefingView briefing={b} />}
      {b && stale && (
        <p className="br-note br-note--warn">Nothing newer since {formatWhen(b.startedAt)}. Check the schedule in <Link to="/briefings?tab=schedule">Briefings</Link>.</p>
      )}
      {state.gaps.length > 0 && (
        <p className="br-note br-note--info">
          {state.gaps.join(' and ')} still {state.gaps.length === 1 ? 'has' : 'have'} placeholders, so the briefings can't judge against them. <Link to="/briefings?tab=context">Fill them in</Link>.
        </p>
      )}
    </section>
  );
}
